import * as React from "react";

import { getCaretRect } from "@/shared/dom";

/**
 * The in-progress `/token` at the caret that a caret autocomplete acts on.
 * Both the skill-slug detector and the topic-reference detector produce this
 * shape, which is what lets them share one autocomplete engine.
 */
export interface CaretToken {
  /** Offset of the leading `/`. Replaced from here to the caret on accept. */
  slashStart: number;
  /** Text after the `/` up to the caret — the filter query. */
  query: string;
}

/** Open-menu state for a caret-anchored autocomplete. */
export interface CaretAutocompleteState {
  /** Offset of the leading `/` of the in-progress token. */
  slashStart: number;
  /** Caret position when the menu (re)opened. */
  cursor: number;
  /** Post-slash filter text (may include a `.field` fragment). */
  query: string;
  /** Highlighted suggestion index. */
  highlight: number;
}

interface Params {
  onChange: (next: string) => void;
  textareaRef: React.RefObject<HTMLTextAreaElement | null>;
  overlayRef: React.RefObject<HTMLElement | null>;
  paperRef: React.RefObject<HTMLElement | null>;
  /** Detect the in-progress token at ``cursor`` (e.g. a `/slug` or `/topic`). */
  detectToken: (text: string, cursor: number) => CaretToken | null;
  /**
   * Optional selector for elements that should NOT close the menu on mousedown
   * (beyond the textarea and the suggestions paper) — e.g. a portaled MUI
   * ``Select`` listbox nested in a suggestion row.
   */
  keepOpenSelector?: string;
}

export interface CaretAutocomplete {
  autocomplete: CaretAutocompleteState | null;
  /** Move the highlight to ``index`` (no-op when the menu is closed). */
  setHighlight: (index: number) => void;
  /**
   * Keep the highlight within ``[0, count)`` as the filtered list shrinks. Wire
   * in an effect keyed on the suggestion count; the count lives with the
   * consumer's suggestion computation, so it is passed in rather than owned.
   */
  clampHighlight: (count: number) => void;
  /** Recompute open/closed from the textarea's text + caret. */
  refresh: (text: string, cursor: number) => void;
  /** Close the menu. */
  close: () => void;
  /** Virtual anchor for a caret-following Popper (null when closed). */
  anchorEl: HTMLElement | null;
  /** Keep the overlay's scroll offset in lockstep with the textarea. */
  syncOverlayScroll: () => void;
  /**
   * Handle Arrow/Enter/Tab/Escape while the menu is open, calling ``onAccept``
   * with the highlighted index on Enter/Tab. ``count`` is the current
   * suggestion count (for wrap-around). Returns true if it consumed the event,
   * so a caller with its own Enter behavior (e.g. submit) can bail.
   */
  handleNavKeyDown: (
    event: React.KeyboardEvent,
    count: number,
    onAccept: (index: number) => void,
  ) => boolean;
  /**
   * Replace the active token — recomputed live from the textarea, not from the
   * captured open-time offsets — with ``insertText`` (plus a trailing space when
   * needed), then restore focus and place the caret after the insertion.
   */
  accept: (insertText: string) => void;
}

/**
 * Stable ids wiring the ARIA combobox relationship between a caret-autocomplete
 * textarea and its suggestion listbox. The textarea references these via
 * ``aria-controls`` / ``aria-activedescendant``; the consumer that renders the
 * menu marks its listbox and options with the matching ids.
 */
export function useComboboxIds(): {
  listboxId: string;
  optionId: (index: number) => string;
} {
  const base = React.useId();
  return React.useMemo(
    () => ({
      listboxId: `${base}-listbox`,
      optionId: (index: number) => `${base}-opt-${index}`,
    }),
    [base],
  );
}

/**
 * The shared state machine behind a caret-anchored `/`-autocomplete over a
 * textarea: open/close tracking, keyboard navigation, highlight clamping,
 * outside-click dismissal, overlay scroll sync, a caret-following Popper anchor,
 * and token-replacing accept. Consumers supply how a token is detected and how
 * many suggestions are shown, and keep their own suggestion computation and
 * menu/overlay rendering. Used by both the chat slug composer and the skill-body
 * topic editor so the mechanics live in one place.
 */
export function useCaretAutocomplete({
  onChange,
  textareaRef,
  overlayRef,
  paperRef,
  detectToken,
  keepOpenSelector,
}: Params): CaretAutocomplete {
  const [autocomplete, setAutocomplete] =
    React.useState<CaretAutocompleteState | null>(null);

  const close = React.useCallback(() => setAutocomplete(null), []);

  const setHighlight = React.useCallback((index: number) => {
    setAutocomplete((prev) =>
      prev === null ? null : { ...prev, highlight: index },
    );
  }, []);

  const clampHighlight = React.useCallback((count: number) => {
    setAutocomplete((prev) =>
      prev !== null && prev.highlight >= count
        ? { ...prev, highlight: Math.max(0, count - 1) }
        : prev,
    );
  }, []);

  const refresh = React.useCallback(
    (text: string, cursor: number) => {
      const active = detectToken(text, cursor);
      if (active === null) {
        setAutocomplete(null);
        return;
      }
      setAutocomplete((prev) => ({
        slashStart: active.slashStart,
        cursor,
        query: active.query,
        // Preserve the highlight while typing into the same open token; reset
        // when a different token starts.
        highlight: prev?.slashStart === active.slashStart ? prev.highlight : 0,
      }));
    },
    [detectToken],
  );

  // Close on outside click. The textarea and the suggestions paper are carved
  // out so clicking a menu row still accepts before this fires; an optional
  // selector carves out nested portaled popovers (e.g. a Select listbox).
  React.useEffect(() => {
    if (autocomplete === null) {
      return;
    }
    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (target === null) {
        return;
      }
      if (paperRef.current?.contains(target)) {
        return;
      }
      if (textareaRef.current?.contains(target)) {
        return;
      }
      if (keepOpenSelector !== undefined && target.closest(keepOpenSelector)) {
        return;
      }
      setAutocomplete(null);
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [autocomplete, paperRef, textareaRef, keepOpenSelector]);

  function syncOverlayScroll() {
    const ta = textareaRef.current;
    const overlay = overlayRef.current;
    if (ta === null || overlay === null) {
      return;
    }
    overlay.scrollTop = ta.scrollTop;
    overlay.scrollLeft = ta.scrollLeft;
  }

  function handleNavKeyDown(
    event: React.KeyboardEvent,
    count: number,
    onAccept: (index: number) => void,
  ): boolean {
    if (autocomplete === null || count === 0) {
      return false;
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setAutocomplete({
        ...autocomplete,
        highlight: (autocomplete.highlight + 1) % count,
      });
      return true;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      setAutocomplete({
        ...autocomplete,
        highlight: (autocomplete.highlight - 1 + count) % count,
      });
      return true;
    }
    if (event.key === "Enter" || event.key === "Tab") {
      event.preventDefault();
      onAccept(autocomplete.highlight);
      return true;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      setAutocomplete(null);
      return true;
    }
    return false;
  }

  function accept(insertText: string) {
    const ta = textareaRef.current;
    if (ta === null) {
      return;
    }
    // Recompute the token from the live textarea rather than trusting the
    // captured open-time offsets: the caret may have moved between opening the
    // menu and accepting, and a stale slashStart would splice at the wrong spot
    // (e.g. doubling the leading slash).
    const cursor = ta.selectionEnd ?? ta.value.length;
    const active = detectToken(ta.value, cursor);
    if (active === null) {
      setAutocomplete(null);
      return;
    }
    const before = ta.value.slice(0, active.slashStart);
    const after = ta.value.slice(cursor);
    const needsTrailingSpace = after.length === 0 || !/^\s/.test(after);
    const insertion = insertText + (needsTrailingSpace ? " " : "");
    onChange(before + insertion + after);
    setAutocomplete(null);
    const caret = active.slashStart + insertion.length;
    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(caret, caret);
    });
  }

  const anchorEl = React.useMemo(() => {
    if (autocomplete === null) {
      return null;
    }
    return {
      // Popper calls getBoundingClientRect on its own update cycle; we read the
      // textarea ref + computed caret rect at call time so wrapping text and
      // window resizes are reflected without explicit value-deps churn.
      getBoundingClientRect: () => {
        const ta = textareaRef.current;
        return ta === null
          ? new DOMRect(0, 0, 0, 0)
          : getCaretRect(ta, autocomplete.slashStart);
      },
      // Popper expects an HTMLElement shape; nodeType: 1 satisfies the duck-test
      // without returning a real DOM node.
      nodeType: 1,
    } as unknown as HTMLElement;
    // textareaRef is a stable ref object; listed to satisfy exhaustive-deps.
  }, [autocomplete, textareaRef]);

  return {
    autocomplete,
    setHighlight,
    clampHighlight,
    refresh,
    close,
    anchorEl,
    syncOverlayScroll,
    handleNavKeyDown,
    accept,
  };
}
